import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  Alert,
  Skeleton,
  Card,
  CardContent,
  Grid
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CardGiftcard as VoucherIcon,
  AttachMoney as MoneyIcon,
  CalendarToday as CalendarIcon,
  Public as OnlineIcon,
  Store as StoreIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import apiClient from '../../api/client';
import { formatCurrency } from '../../utils/currency';
import DevOnly from '../../components/DevOnly';

const VouchersManagement = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');

  // Fetch vouchers
  const { data: vouchersData, isLoading, error } = useQuery({
    queryKey: ['vouchers', page, rowsPerPage, searchQuery],
    queryFn: async () => {
      try {
        const response = await apiClient.get('/financial/vouchers', {
          params: {
            skip: page * rowsPerPage,
            limit: rowsPerPage,
            search: searchQuery || undefined
          }
        });
        return response.data;
      } catch (error) {
        console.error('Error fetching vouchers:', error);
        return { vouchers: [], total: 0, stats: {} };
      }
    },
    staleTime: 5 * 60 * 1000,
  });

  const vouchers = vouchersData?.vouchers || [];
  const total = vouchersData?.total || 0;
  const stats = vouchersData?.stats || {};

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearch = (event) => {
    setSearchQuery(event.target.value);
    setPage(0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'No expiry';
    return new Date(dateString).toLocaleDateString();
  };

  const getExpiryStatus = (expiryDate) => {
    if (!expiryDate) return null;
    
    const today = new Date();
    const expiry = new Date(expiryDate);
    const daysUntilExpiry = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
    
    if (daysUntilExpiry < 0) {
      return { label: 'Expired', color: 'error' };
    } else if (daysUntilExpiry <= 30) {
      return { label: `${daysUntilExpiry} days left`, color: 'warning' };
    } else {
      return { label: 'Valid', color: 'success' };
    }
  };

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Failed to load gift vouchers. Please try again later.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Gift Vouchers Management
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage gift vouchers and promotional certificates
          </Typography>
        </Box>
        <DevOnly>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/vouchers/create')}
            size="large"
          >
            Create Voucher
          </Button>
        </DevOnly>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <VoucherIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography color="text.secondary" variant="body2">
                  Total Vouchers
                </Typography>
              </Box>
              <Typography variant="h4">
                {isLoading ? <Skeleton width={60} /> : total}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <MoneyIcon sx={{ mr: 1, color: 'success.main' }} />
                <Typography color="text.secondary" variant="body2">
                  Total Value
                </Typography>
              </Box>
              <Typography variant="h4">
                {isLoading ? <Skeleton width={100} /> : formatCurrency(stats.totalValue || 0)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <OnlineIcon sx={{ mr: 1, color: 'info.main' }} />
                <Typography color="text.secondary" variant="body2">
                  Online Vouchers
                </Typography>
              </Box>
              <Typography variant="h4">
                {isLoading ? <Skeleton width={60} /> : stats.online || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <CalendarIcon sx={{ mr: 1, color: 'warning.main' }} />
                <Typography color="text.secondary" variant="body2">
                  Expiring Soon
                </Typography>
              </Box>
              <Typography variant="h4">
                {isLoading ? <Skeleton width={60} /> : stats.expiringSoon || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Search Bar */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Search vouchers by name..."
          value={searchQuery}
          onChange={handleSearch}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      {/* Vouchers Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Voucher Name</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Valid For</TableCell>
                <TableCell>Expiry Date</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Availability</TableCell>
                <TableCell>Theme</TableCell>
                <TableCell>Created Date</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                Array.from({ length: 5 }).map((_, index) => (
                  <TableRow key={`skeleton-${index}`}>
                    {Array.from({ length: 9 }).map((_, cellIndex) => (
                      <TableCell key={`skeleton-cell-${cellIndex}`}>
                        <Skeleton variant="text" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : vouchers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <Typography variant="body1" sx={{ py: 3 }}>
                      No gift vouchers found. Create your first voucher to get started.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                vouchers.map((voucher) => {
                  const expiryStatus = getExpiryStatus(voucher.expiry_date);
                  return (
                    <TableRow
                      key={voucher.id}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/vouchers/${voucher.id}`)}
                    >
                      <TableCell>
                        <Typography variant="body1" fontWeight="medium">
                          {voucher.name || 'Gift Voucher'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body1" fontWeight="medium" color="success.main">
                          {formatCurrency(voucher.amount || 0)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {voucher.valid_for ? `${voucher.valid_for} days` : 'No limit'}
                      </TableCell>
                      <TableCell>
                        {formatDate(voucher.expiry_date)}
                      </TableCell>
                      <TableCell>
                        {expiryStatus && (
                          <Chip
                            label={expiryStatus.label}
                            color={expiryStatus.color}
                            size="small"
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={voucher.online ? 'Online' : 'In-Store'}
                          color={voucher.online ? 'primary' : 'default'}
                          size="small"
                          variant="outlined"
                          icon={voucher.online ? <OnlineIcon /> : <StoreIcon />}
                        />
                      </TableCell>
                      <TableCell>
                        {voucher.theme ? (
                          <Chip
                            label={voucher.theme}
                            size="small"
                            variant="outlined"
                          />
                        ) : (
                          'Default'
                        )}
                      </TableCell>
                      <TableCell>
                        {voucher.created_at ? 
                          new Date(voucher.created_at).toLocaleDateString() : 
                          'N/A'
                        }
                      </TableCell>
                      <TableCell align="center">
                        <DevOnly>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/vouchers/${voucher.id}/edit`);
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              // Handle delete
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </DevOnly>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {vouchers.length > 0 && (
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={total}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        )}
      </Paper>
    </Box>
  );
};

export default VouchersManagement;